"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Target, DollarSign, CircleCheck as CheckCircle2, ArrowUpRight, ChartBar as BarChart3, ShieldCheck, Building2, PhoneCall } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const impactPillars = [{
  id: 1,
  title: "Qualified Pipeline Growth",
  description: "Target high-intent search queries that drive sales-qualified leads directly to your business pipeline, rather than vanity keyword volumes.",
  metric: "High Intent",
  metricLabel: "Bottom-of-Funnel Focus",
  icon: "Target"
}, {
  id: 2,
  title: "Decreased CAC & Paid Reliance",
  description: "Build organic equity so your acquisition cost drops consistently over time, freeing your business from ever-increasing PPC ad bidding wars.",
  metric: "Sustainable",
  metricLabel: "Lower Customer Acquisition Cost",
  icon: "TrendingUp"
}, {
  id: 3,
  title: "Direct Strategic Accountability",
  description: "Work directly with an experienced consultant to turn search insights into rapid technical execution, faster ranking gains, and tangible commercial returns.",
  metric: "100%",
  metricLabel: "Direct Senior Consultant",
  icon: "ShieldCheck"
}, {
  id: 4,
  title: "Measurable Revenue Contribution",
  description: "Clear tracking from organic entry points to completed transactions, quote requests, and inbound phone consultations.",
  metric: "Commercial",
  metricLabel: "Attributed Revenue Focus",
  icon: "DollarSign"
}];
const commercialFramework = [{
  stepNumber: "01",
  title: "Revenue-Focused Keyword Mapping",
  detail: "Identifying commercial searches with immediate buyer motivation across national and local markets."
}, {
  stepNumber: "02",
  title: "Technical Infrastructure Fixes",
  detail: "Resolving crawl budget, indexing barriers, and Core Web Vitals to maximize search engine discoverability."
}, {
  stepNumber: "03",
  title: "Content & Entity Authority",
  detail: "Publishing expert, structured content that satisfies Google algorithms and emerging AI search engines."
}, {
  stepNumber: "04",
  title: "Transparent Attribution Reporting",
  detail: "Direct, plain-English monthly reporting focusing strictly on conversions, rankings, and lead volume."
}];
export default function Impact() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="commercialimpact" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Commercial Impact &amp; ROI
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
            SEO Engineered for Bottom-Line Business Growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            I don&apos;t focus on vanity traffic metrics or empty ranking screenshots. My consulting strategy is designed around tangible customer acquisition, higher lead conversion rates, and sustainable commercial returns.
          </p>
        </div>

        {/* 4 Commercial Pillars */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {impactPillars.map((pillar, i) => <motion.div key={pillar.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between p-6">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      {pillar.icon === "Target" && <Target className="w-6 h-6" />}
                      {pillar.icon === "TrendingUp" && <TrendingUp className="w-6 h-6" />}
                      {pillar.icon === "ShieldCheck" && <ShieldCheck className="w-6 h-6" />}
                      {pillar.icon === "DollarSign" && <DollarSign className="w-6 h-6" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground uppercase px-2 py-1 rounded bg-muted">
                      {pillar.metric}
                    </span>
                  </div>

                  <CardTitle className="text-xl font-bold font-default text-card-foreground mb-3">
                    {pillar.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </CardDescription>
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <span className="text-xs font-medium text-primary block">
                    {pillar.metricLabel}
                  </span>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Strategic Comparison & Value Block */}
        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 lg:p-12 mb-16 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <Badge variant="outline" className="mb-3 text-xs">
                Direct Consulting Advantage
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-4">
                Maximising Organic Revenue Through Direct Strategic Execution
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                Every technical audit, keyword architecture, and content roadmap is engineered to drive commercial conversions. Working directly with me ensures your business benefits from over a decade of search expertise focused entirely on measurable pipeline and sales growth.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-medium">Clear commercial KPIs aligned with sales goals</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-medium">Direct technical communication with your developers</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-medium">Agile tactical pivots without agency red tape</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-medium">No inflated hourly rates or hidden overheads</span>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Button asChild size="lg" className="shadow-sm">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                    <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {commercialFramework.map((item, i) => <div key={item.stepNumber} data-index={i} className="p-5 rounded-xl bg-background border border-border flex flex-col justify-between">
                    <div>
                      <span className="text-primary font-mono text-xs font-bold px-2 py-1 bg-primary/10 rounded inline-block mb-3">
                        {item.stepNumber}
                      </span>
                      <h4 className="text-base font-bold text-foreground mb-2 font-default">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Action Banner */}
        <div className="rounded-xl bg-primary text-primary-foreground p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="space-y-2 text-center md:text-left">
            <h4 className="text-xl sm:text-2xl font-bold font-default text-primary-foreground">
              Ready to turn organic search into a predictable revenue channel?
            </h4>
            <p className="text-sm text-primary-foreground/80 max-w-2xl">
              Let&apos;s review your current organic performance and identify immediate growth opportunities for your website.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/ContactPage">
                Schedule Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20">
              <a href="tel:+447490984430">
                <PhoneCall className="w-4 h-4 mr-2" />
                +44 7490984430
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}